// src/components/comunicacion/CalendarioCorporativo.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  getStartOfWeekCalendar,
  toLocalDateTimeString,
  parseDateTimeFlexible,
  getLiteralHours,
  getLiteralDateComponents
} from "../../../../utils/DateHelpers";
import axios from "axios";
import { ENDPOINTS } from "../../Utils/Endpoints";
import { getSessionUserId } from "../../../../utils/SessionUser";
import { ENDPOINTS as SEC_ENDPOINTS } from "../../../Seguridad/Utils/Endpoints"; // Import
import { personasNormalize } from "../../../General/Objetos/PersonasModel"; // Import
import AlertDialog from "../../../Lms/Components/Shared/AlertDialog";
import ConfirmDeleteDialog from "../../../security/ConfirmDeleteDialog";

const START_HOUR = 0; // Medianoche  
const END_HOUR = 24; // Hasta medianoche del siguiente da
const HOURS = Array.from(
  { length: END_HOUR - START_HOUR },
  (_, i) => START_HOUR + i
);

const CATEGORIES = [
  { value: "Corporativo", label: "Corporativo" },
  { value: "Capacitación", label: "Capacitación" },
  { value: "RH", label: "Recursos Humanos" },
  { value: "Finanzas", label: "Finanzas" },
  { value: "Auditoría", label: "Auditoría" },
  { value: "Cumpleaños", label: "Cumpleaños" },
  { value: "Festivo", label: "Días Festivos" },
];

const CATEGORY_CLASS = {
  Corporativo: "corporativo",
  Capacitación: "capacitacion",
  RH: "rh",
  Finanzas: "finanzas",
  Auditoría: "auditoria",
  Cumpleaños: "cumpleanos",
  Festivo: "festivo",
};

const dayNamesShort = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];

// Helper para capitalizar primera letra
const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);

const formatFullDate = (date) => {
  const dayNames = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];
  const monthNames = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];

  const dayOfWeek = dayNames[date.getDay()];
  const day = date.getDate();
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear();

  return `${dayOfWeek} ${day} de ${capitalize(month)} de ${year}`;
};

// === COMPONENTE ===

const CalendarioCorporativo = ({ onNavigate }) => {
  const [eventos, setEventos] = useState([]);
  const [dataLoaded, setDataLoaded] = useState(false);

  // Vista: "week" | "month"
  const [viewMode, setViewMode] = useState("month");

  // Filtro de categora
  const [filtroCategoria, setFiltroCategoria] = useState("Todas");

  // Fecha ancla para navegacin (mes/semana)
  const [anchorDate, setAnchorDate] = useState(() => new Date());

  const [alertDialog, setAlertDialog] = useState({ open: false, title: "", message: "", variant: "info" });
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);

  const showAlertDialog = (title, message, variant = 'info') => {
    setAlertDialog({
      open: true,
      title,
      message,
      variant,
      onClose: () => setAlertDialog(prev => ({ ...prev, open: false }))
    });
  };

  // Forzar actualización a fecha actual al montar (corrige stale state en hot-reload)
  useEffect(() => {
    setAnchorDate(new Date());
  }, []);

  const [selectedEventId, setSelectedEventId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [eventoSeleccionado, setEventoSeleccionado] = useState(null);

  const [formData, setFormData] = useState(() => {
    const now = new Date();
    const fin = new Date(now.getTime() + 60 * 60 * 1000);
    return {
      titulo: "",
      descripcion: "",
      categoria: "Corporativo",
      ubicacion: "",
      responsable: "",
      fechaInicio: toLocalDateTimeString(now),
      fechaFin: toLocalDateTimeString(fin),
    };
  });

  const [errores, setErrores] = useState({});

  // === HELPERS MOVIDOS AL INICIO PARA EVITAR REFERENCE ERROR ===

  const getCategoriaClass = (categoria) => {
    if (!categoria) return "corporativo";
    const cat = categoria.toString().trim().toLowerCase();

    if (cat.includes("capacita")) return "capacitacion";
    if (cat === "rh" || cat.includes("recursos")) return "rh";
    if (cat.includes("finanza")) return "finanzas";
    if (cat.includes("auditor")) return "auditoria";
    if (cat.includes("cumple") || cat.includes("birthday")) return "cumpleanos";
    if (cat.includes("festiv") || cat.includes("holiday")) return "festivo";

    return "corporativo";
  };

  const getCategoriaStyles = (catName) => {
    const cls = getCategoriaClass(catName);
    switch (cls) {
      case 'corporativo': return { bg: '#DBEAFE', text: '#1E40AF', border: '#3B82F6' };
      case 'capacitacion': return { bg: '#FFEDD5', text: '#9A3412', border: '#F97316' };
      case 'rh': return { bg: '#F3E8FF', text: '#6B21A8', border: '#8B5CF6' };
      case 'finanzas': return { bg: '#DCFCE7', text: '#166534', border: '#10B981' };
      case 'auditoria': return { bg: '#FEE2E2', text: '#991B1B', border: '#EF4444' };
      case 'cumpleanos': return { bg: '#FCE7F3', text: '#9D174D', border: '#F472B6' };
      case 'festivo': return { bg: '#CCFBF1', text: '#0F766E', border: '#14B8A6' };
      default: return { bg: '#F1F5F9', text: '#475569', border: '#94A3B8' };
    }
  };

  const HOUR_BLOCK_PX = 46;

  const getEventStyle = (ev) => {
    const inicio = parseDateTimeFlexible(ev.fechaInicio);
    const fin = parseDateTimeFlexible(ev.fechaFin);
    if (!inicio || !fin) return { top: 0, height: HOUR_BLOCK_PX };

    const startHour = getLiteralHours(ev.fechaInicio);
    const endHour = getLiteralHours(ev.fechaFin);
    const top = (startHour - START_HOUR) * HOUR_BLOCK_PX;
    const height = Math.max(
      (endHour - startHour) * HOUR_BLOCK_PX,
      HOUR_BLOCK_PX / 2
    );

    return {
      top: `${top}px`,
      height: `${height}px`,
    };
  };

  const formatHourLabel = (hour) => {
    const suffix = hour < 12 ? "AM" : "PM";
    const h = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${h} ${suffix}`;
  };

  const isSameDay = (d1, d2) => {
    return (
      d1.getFullYear() === d2.getFullYear() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getDate() === d2.getDate()
    );
  };

  // === CARGA / GUARDADO PERSISTENTE ===

  const MAP_FRONT_TO_DB = {
    "RH": "Recursos Humanos",
    "Capacitación": "Capacitación",
    "Auditoría": "Auditoría",
    "Cumpleaños": "Cumpleaños",
    "Festivo": "Festivo",
    "Corporativo": "Corporativo",
    "Finanzas": "Finanzas"
  };

  const MAP_DB_TO_FRONT = {
    "Recursos Humanos": "RH",
    "Finanzas": "Finanzas",
    "Auditoría": "Auditoría",
    "Cumpleaños": "Cumpleaños",
    "Festivo": "Festivo",
    "Capacitación": "Capacitación",
    "Corporativo": "Corporativo",
    "RH": "RH",
    "Días Festivos": "Festivo"
  };

  const [dbCategorias, setDbCategorias] = useState([]); // [{ idCategoria, nombre, ... }]
  const [areasNegocio, setAreasNegocio] = useState([]); // [{ idAreaNegocio, nombre, ... }]

  // Cargar Categoras, reas y Eventos
  useEffect(() => {
    const cargarTodo = async () => {
      try {
        const idUsr = getSessionUserId();

        // 1. CARGA PARALELA (Categorías, Areas, Eventos, Personas, Usuarios, Empleados, Unidades)
        const [resCat, resAreas, resEv, resPersonas, resUsuarios, resEmpleados, resUnidades] = await Promise.all([
          axios.get(ENDPOINTS.categorias).catch(() => ({ data: [] })),
          axios.get(ENDPOINTS.areasNegocios).catch(() => ({ data: [] })),
          axios.get(ENDPOINTS.eventos),
          axios.get(ENDPOINTS.personas),
          axios.get(SEC_ENDPOINTS.usuarios),
          axios.get(SEC_ENDPOINTS.empleados),
          axios.get(SEC_ENDPOINTS.unidadesOrgEmpleado)
        ]);

        const rawCats = Array.isArray(resCat.data) ? resCat.data : [];
        const normalizedCats = rawCats.map(c => ({
          idCategoria: c.idCategoria ?? c.IdCategoria ?? 0,
          nombre: c.nombre ?? c.Nombre ?? "Sin categoría"
        }));
        setDbCategorias(normalizedCats);

        const rawAreas = Array.isArray(resAreas.data) ? resAreas.data : [];
        const normalizedAreas = rawAreas.map(a => ({
          idAreaNegocio: a.idAreaNegocio ?? a.IdAreaNegocio ?? 0,
          nombre: a.nombre ?? a.Nombre ?? "Sin nombre"
        }));
        setAreasNegocio(normalizedAreas);

        // A. EVENTOS ESTÁNDAR
        const rawEventos = resEv.data || [];
        const eventosHydrated = rawEventos.map(e => {
          const idCat = e.idCategoria ?? e.IdCategoria;
          const catObj = normalizedCats.find(c => c.idCategoria === idCat);
          let catName = catObj ? catObj.nombre : "Corporativo";
          catName = MAP_DB_TO_FRONT[catName] || catName;

          return {
            id: e.idEvento ?? e.IdEvento,
            titulo: e.titulo ?? e.Titulo,
            descripcion: e.descripcion ?? e.Descripcion,
            categoria: catName,
            ubicacion: e.ubicacion ?? e.Ubicacion,
            responsable: e.responsable ?? e.Responsable,
            fechaInicio: e.fechaInicio ?? e.FechaInicio,
            fechaFin: e.fechaFin ?? e.FechaFin,
            _idCategoria: idCat
          };
        });

        // B. CUMPLEAÑOS (Logic from CalendarioUsuario.jsx)
        const listaUsuarios = resUsuarios.data || [];
        const listaEmpleados = resEmpleados.data || [];
        const listaUnidades = resUnidades.data || [];
        const listaPersonas = (resPersonas.data || []).map(p => personasNormalize(p));

        // B.1 Mis Unidades
        let miUnidadIds = new Set();
        const miUsuario = listaUsuarios.find(u => String(u.idUsuario) === String(idUsr));
        if (miUsuario) {
          const miEmpleado = listaEmpleados.find(e => e.idPersona === miUsuario.idPersona);
          if (miEmpleado) {
            listaUnidades
              .filter(u => u.idEmpleado === miEmpleado.idEmpleado)
              .forEach(u => miUnidadIds.add(u.idUnidadOrganizacional));
          }
        }

        // B.2 Mapas de Seguridad
        const empMap = {};
        listaUnidades.forEach(u => {
          if (!empMap[u.idEmpleado]) empMap[u.idEmpleado] = [];
          empMap[u.idEmpleado].push(u.idUnidadOrganizacional);
        });

        const personaUnidadMap = {};
        listaEmpleados.forEach(e => {
          if (empMap[e.idEmpleado]) personaUnidadMap[e.idPersona] = new Set(empMap[e.idEmpleado]);
        });

        const checkIntersection = (targetSet) => {
          // If no units assigned to me, I see nothing (or I see all? Assume strict unit visibility)
          // For CalendarioCorporativo, usually Admin sees all, but let's stick to strict unit visibility if desired by user, 
          // OR if this is "General", maybe everyone sees everyone? 
          // Re-reading user request: "respetcivas" implies relative to me.
          if (miUnidadIds.size === 0 || !targetSet || targetSet.size === 0) return false;
          for (let uId of targetSet) {
            if (miUnidadIds.has(uId)) return true;
          }
          return false;
        };

        // B.3 Generar Eventos de Cumpleaños
        const hoy = new Date();
        const yearCurrent = hoy.getFullYear();
        const yearNext = yearCurrent + 1;
        const yearsToGen = [yearCurrent, yearNext];

        const cumpleanosEvents = [];

        listaPersonas.forEach(p => {
          if (!p.fechaNacimiento) return;
          // Mostrar si comparten unidad
          if (!checkIntersection(personaUnidadMap[p.idPersona])) return;

          const fechaNacParts = new Date(p.fechaNacimiento);
          const mes = fechaNacParts.getUTCMonth();
          const dia = fechaNacParts.getUTCDate();

          yearsToGen.forEach(y => {
            const fechaCumple = new Date(y, mes, dia, 9, 0, 0); // 9 AM
            const fechaFin = new Date(y, mes, dia, 11, 0, 0); // 11 AM

            cumpleanosEvents.push({
              id: `bday-${p.idPersona}-${y}`,
              titulo: `🎂 Cumpleaños de ${p.nombre} ${p.apellidoPaterno || ''} ${p.apellidoMaterno || ''}`.trim(),
              descripcion: `¡Hoy es el cumpleaños de ${p.nombre} ${p.apellidoPaterno || ''} ${p.apellidoMaterno || ''}! Envíale una felicitación.`.replace(/\s+/g, ' ').trim(),
              categoria: "Cumpleaños",
              ubicacion: "Oficina / Remoto",
              responsable: "Capital Humano",
              fechaInicio: fechaCumple.toISOString(),
              fechaFin: fechaFin.toISOString()
            });
          });
        });

        // D. MERGE
        const todosEventos = [...eventosHydrated, ...cumpleanosEvents];
        setEventos(todosEventos);
        setDataLoaded(true);

      } catch (error) {
        console.error("Error cargando calendario:", error);
      }
    };

    cargarTodo();
  }, []);

  const recargarEventos = async () => {
    try {
      const resEv = await axios.get(ENDPOINTS.eventos);
      const rawEventos = resEv.data || [];

      const eventosHydrated = rawEventos.map(e => {
        // El API devuelve PascalCase (PropertyNamingPolicy=null). Usamos ?? para ambas variantes.
        const idCat = e.IdCategoria ?? e.idCategoria;
        const catObj = dbCategorias.find(c =>
          (c.idCategoria ?? c.IdCategoria) === idCat
        );
        let catName = catObj ? (catObj.nombre ?? catObj.Nombre) : "Corporativo";
        catName = MAP_DB_TO_FRONT[catName] || catName;

        return {
          id: e.IdEvento ?? e.idEvento,
          titulo: e.Titulo ?? e.titulo,
          descripcion: e.Descripcion ?? e.descripcion,
          categoria: catName,
          ubicacion: e.Ubicacion ?? e.ubicacion,
          responsable: e.Responsable ?? e.responsable,
          fechaInicio: e.FechaInicio ?? e.fechaInicio,
          fechaFin: e.FechaFin ?? e.fechaFin,
          _idCategoria: idCat
        };
      });

      // Preservar los eventos de cumpleaños (generados en cliente, no vienen del API)
      setEventos(prev => {
        const cumpleanos = prev.filter(ev => String(ev.id).startsWith('bday-'));
        return [...eventosHydrated, ...cumpleanos];
      });
    } catch (error) {
      console.error("Error recargando eventos:", error);
    }
  };

  // Eliminamos el efecto de guardado automtico local
  // useEffect(() => { if (!dataLoaded) return; saveEventosCalendario(eventos); }, [eventos, dataLoaded]);

  // === HELPERS DE FECHAS ===

  const weekStart = useMemo(
    () => getStartOfWeekCalendar(anchorDate),
    [anchorDate]
  );

  const daysOfWeek = useMemo(() => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(weekStart);
      d.setDate(d.getDate() + i);
      days.push(d);
    }
    return days;
  }, [weekStart]);

  const monthMatrix = useMemo(() => {
    const year = anchorDate.getFullYear();
    const month = anchorDate.getMonth();
    const firstOfMonth = new Date(year, month, 1);
    const startGrid = getStartOfWeekCalendar(firstOfMonth);

    const weeks = [];
    let current = new Date(startGrid);

    // Siempre pintamos 6 semanas (6 filas)
    for (let w = 0; w < 6; w++) {
      const week = [];
      for (let d = 0; d < 7; d++) {
        week.push(new Date(current));
        current.setDate(current.getDate() + 1);
      }
      weeks.push(week);
    }
    return weeks;
  }, [anchorDate]);

  const eventosSemana = useMemo(() => {
    const start = new Date(weekStart);
    start.setHours(0, 0, 0, 0);
    const end = new Date(weekStart);
    end.setDate(end.getDate() + 7);
    end.setHours(0, 0, 0, 0);

    const filtrados = filtroCategoria === "Todas"
      ? eventos
      : eventos.filter(ev => getCategoriaClass(ev.categoria) === getCategoriaClass(filtroCategoria));

    return filtrados.filter((ev) => {
      const comps = getLiteralDateComponents(ev.fechaInicio);
      if (!comps) return false;
      const d = new Date(comps.year, comps.month - 1, comps.day);
      return d >= start && d < end;
    });
  }, [eventos, weekStart, filtroCategoria]);

  const eventosPorDiaMes = useMemo(() => {
    const map = new Map(); // key: YYYY-MM-DD, value: array de eventos

    const filtrados = filtroCategoria === "Todas"
      ? eventos
      : eventos.filter(ev => getCategoriaClass(ev.categoria) === getCategoriaClass(filtroCategoria));

    filtrados.forEach((ev) => {
      const comps = getLiteralDateComponents(ev.fechaInicio);
      if (!comps) return;
      const key = `${comps.year}-${comps.month.toString().padStart(2, "0")}-${comps.day.toString().padStart(2, "0")}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(ev);
    });

    // orden visual opcional
    for (const arr of map.values()) {
      arr.sort((a, b) => {
        const da = parseDateTimeFlexible(a.fechaInicio) || new Date();
        const db = parseDateTimeFlexible(b.fechaInicio) || new Date();
        return da - db;
      });
    }

    return map;
  }, [eventos, filtroCategoria]);

  // === NAV ===

  const goToToday = () => {
    const now = new Date();
    setAnchorDate(now);
  };

  const changePeriod = (delta) => {
    setAnchorDate((prev) => {
      const d = new Date(prev);
      if (viewMode === "week") {
        d.setDate(d.getDate() + delta * 7);
      } else {
        d.setMonth(d.getMonth() + delta);
      }
      return d;
    });
  };

  const handleChangeViewMode = (mode) => {
    setViewMode(mode);
  };

  const handleSelectDayFromMonth = (dayDate) => {
    setAnchorDate(dayDate);
    setViewMode("week");
  };

  // === FORM ===

  const resetFormToNewEvent = () => {
    const base = new Date(anchorDate);
    const start = new Date(base);
    start.setHours(START_HOUR, 0, 0, 0);
    const end = new Date(start.getTime() + 60 * 60 * 1000);

    setSelectedEventId(null);
    setFormData({
      titulo: "",
      descripcion: "",
      categoria: "Corporativo",
      ubicacion: "",
      responsable: "",
      fechaInicio: toLocalDateTimeString(start),
      fechaFin: toLocalDateTimeString(end),
    });
    setErrores({});
  };

  const resetForm = resetFormToNewEvent; // Alias for consistency with the provided change

  const handleDateChange = (e) => {
    const val = e.target.value;
    if (!val) {
      setFormData(prev => ({ ...prev, fechaInicio: val }));
      return;
    }
    const dateObj = parseDateTimeFlexible(val);
    if (!dateObj) {
      setFormData(prev => ({ ...prev, fechaInicio: val }));
      return;
    }
    const endObj = new Date(dateObj.getTime() + 60 * 60 * 1000);

    setFormData(prev => ({
      ...prev,
      fechaInicio: val,
      fechaFin: toLocalDateTimeString(endObj)
    }));
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validarFormulario = () => {
    const nuevo = {};
    if (!formData.titulo.trim()) {
      nuevo.titulo = "El título es obligatorio.";
    }
    if (!formData.fechaInicio) {
      nuevo.fechaInicio = "La fecha/hora de inicio es obligatoria.";
    }
    if (!formData.fechaFin) {
      nuevo.fechaFin = "La fecha/hora de fin es obligatoria.";
    }

    const ini = parseDateTimeFlexible(formData.fechaInicio);
    const fin = parseDateTimeFlexible(formData.fechaFin);

    if (ini && fin) {
      if (fin <= ini) {
        nuevo.fechaFin = "La fecha/hora de fin debe ser posterior a la de inicio.";
      }

      // VALIDACIONES DE NEGOCIO (No traslapes, Max 5 events)
      const eventosDia = eventos.filter(ev => {
        if (selectedEventId && ev.id === selectedEventId) return false;
        const evIni = parseDateTimeFlexible(ev.fechaInicio);
        return evIni && isSameDay(evIni, ini);
      });

      // 1. Máximo 2 eventos por día
      if (eventosDia.length >= 2) {
        showAlertDialog("Límite alcanzado", "No se pueden crear más de 2 eventos para un mismo día.", "warning");
        return false;
      }

      // 2. No traslapes (ni por un minuto)
      const traslape = eventosDia.find(ev => {
        const evIni = parseDateTimeFlexible(ev.fechaInicio);
        const evFin = parseDateTimeFlexible(ev.fechaFin);
        if (!evIni || !evFin) return false;

        // Caso traslape: (StartA < EndB) y (EndA > StartB)
        return ini < evFin && fin > evIni;
      });

      if (traslape) {
        showAlertDialog("Conflicto de horario", `El horario seleccionado coincide con el evento: "${traslape.titulo}". No se permite traslape de eventos.`, "error");
        return false;
      }
    }

    setErrores(nuevo);
    return Object.keys(nuevo).length === 0;
  };

  const handleGuardarEvento = async (e) => {
    e.preventDefault();
    if (!validarFormulario()) return;

    try {
      const dbName = MAP_FRONT_TO_DB[formData.categoria] || formData.categoria;

      const normalize = (s) => (s || "").trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

      const catObj = dbCategorias.find(c => {
        const n1 = normalize(c.nombre);
        const n2 = normalize(dbName);
        return n1 === n2 || n1.includes(n2) || n2.includes(n1);
      });

      // 1. Intentar match exacto o normalizado por nombre
      let idCategoria = catObj ? catObj.idCategoria : null;

      // 2. Si no hay match, intentar buscar por similitud en el nombre del frontend
      if (!idCategoria) {
        const softMatch = dbCategorias.find(c => {
          const n1 = normalize(c.nombre);
          const n2 = normalize(formData.categoria);
          return n1 === n2 || n1.includes(n2) || n2.includes(n1);
        });
        if (softMatch) idCategoria = softMatch.idCategoria;
      }

      // 3. Fallback a IDs previos si estamos editando
      if (!idCategoria && eventoSeleccionado && (eventoSeleccionado._idCategoria || eventoSeleccionado.idCategoria)) {
        idCategoria = eventoSeleccionado._idCategoria || eventoSeleccionado.idCategoria;
      }

      // 4. Fallback final a Corporativo
      if (!idCategoria) {
        const corp = dbCategorias.find(c => {
          const n = (c.nombre || "").toLowerCase();
          return n.includes("corp") || n.includes("gen");
        });
        if (corp) idCategoria = corp.idCategoria;
      }

      // ÚLTIMO RECURSO: Usar el primero si nada funcionó
      if (!idCategoria && dbCategorias.length > 0) {
        idCategoria = dbCategorias[0].idCategoria;
      }

      // Evitar el error de SQL enviando un ID válido siempre
      if (!idCategoria) {
        showAlertDialog("Error", "No se encontró una categoría válida para el evento.", "error");
        return;
      }

      // Enviar las fechas en formato ISO LOCAL 'YYYY-MM-DDTHH:mm:ss'
      // Al NO enviar offset ni 'Z', el backend .NET suele tomarlas como locales/sin zona,
      // preservando exactamente lo que el usuario ingresó.
      const formatForBackend = (val) => {
        if (!val) return null;
        let clean = val.replace(" ", "T");
        if (clean.length === 16) clean += ":00";
        return clean;
      };

      const fechaInicioISO = formatForBackend(formData.fechaInicio);
      const fechaFinISO = formatForBackend(formData.fechaFin);

      const payload = {
        titulo: formData.titulo,
        descripcion: formData.descripcion,
        idCategoria: idCategoria,
        ubicacion: formData.ubicacion,
        responsable: formData.responsable,
        fechaInicio: fechaInicioISO,
        fechaFin: fechaFinISO,
        idUsuarioCreador: getSessionUserId(),
        activo: true
      };

      if (selectedEventId) {
        // Update
        payload.idEvento = selectedEventId;
        // PUT
        await axios.put(ENDPOINTS.eventos, payload);
      } else {
        // Create
        await axios.post(ENDPOINTS.eventos, payload);
      }

      showAlertDialog("Éxito", "Evento guardado correctamente", "success");
      setShowModal(false);
      resetForm();

      // Actualizar la lista en memoria en lugar de recargar la pgina
      recargarEventos();

    } catch (error) {
      console.error("Error guardando evento:", error);
      const msg = error.response?.data ? JSON.stringify(error.response.data) : error.message;
      showAlertDialog("Error", `Error al guardar: ${msg}`, "error");
    }
  };

  const handleSeleccionarEvento = (evento) => {
    setSelectedEventId(evento.id);
    setEventoSeleccionado(evento);
    setFormData({
      titulo: evento.titulo,
      descripcion: evento.descripcion,
      categoria: evento.categoria,
      ubicacion: evento.ubicacion,
      responsable: evento.responsable,
      fechaInicio: toLocalDateTimeString(parseDateTimeFlexible(evento.fechaInicio)),
      fechaFin: toLocalDateTimeString(parseDateTimeFlexible(evento.fechaFin)),
    });
    setErrores({});
    setShowModal(true);
  };

  const handleEliminarEvento = () => {
    setShowModal(false); 
    setShowConfirmDelete(true);
  };

  const ejecutarEliminacionEvento = async () => {
    try {
      await axios.delete(`${ENDPOINTS.eventos}/${eventoSeleccionado.id}`);
      showAlertDialog("Éxito", "Evento eliminado", "success");
      setShowModal(false);
      setShowConfirmDelete(false);
      resetForm();
      recargarEventos();
    } catch (error) {
      console.error("Error eliminando evento:", error);
      const msg = error.response?.data ? JSON.stringify(error.response.data) : error.message;
      showAlertDialog("Error", `Error al eliminar: ${msg}`, "error");
      setShowConfirmDelete(false);
    }
  };

  const handleCrearEventoDesdeCelda = (dayDate, hour) => {
    const inicio = new Date(dayDate);
    inicio.setHours(hour, 0, 0, 0);
    const fin = new Date(inicio.getTime() + 60 * 60 * 1000);

    setSelectedEventId(null);
    setFormData({
      titulo: "",
      descripcion: "",
      categoria: "Corporativo",
      ubicacion: "",
      responsable: "",
      fechaInicio: toLocalDateTimeString(inicio),
      fechaFin: toLocalDateTimeString(fin),
    });
    setErrores({});
  };



  // === RENDER ===

  return (
    <div className="intra-calendar-page">
      {/* HEADER */}
      <header className="intra-calendar-header" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '1.5rem' }}>
        {/* FILA 1: TÍTULO Y BOTÓN VOLVER */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {onNavigate && (
              <button
                type="button"
                className="intra-button-ghost btn-cancel"
                onClick={() => onNavigate("dashboard")}
                style={{
                  marginBottom: '0.75rem',
                  width: 'fit-content'
                }}
              >
                <span style={{ fontSize: '1.2rem', lineHeight: '1' }}>←</span>
                <span>Volver al dashboard</span>
              </button>
            )}
            <h1 className="intra-calendar-title" style={{ margin: 0 }}>Calendario corporativo</h1>
          </div>
        </div>

        {/* FILA 2: CONTROLES (TOGGLE, FILTRO, BOTONES) */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '2rem' }}>

          {/* Toggle View Mode */}
          <div
            onClick={() => handleChangeViewMode(viewMode === 'week' ? 'month' : 'week')}
            style={{
              position: 'relative',
              display: 'flex',
              backgroundColor: '#f1f5f9',
              borderRadius: '999px',
              padding: '4px',
              border: '1px solid #e2e8f0',
              height: 'fit-content',
              cursor: 'pointer',
              userSelect: 'none',
              width: '160px',
              flexShrink: 0
            }}
          >
            <div style={{
              position: 'absolute',
              top: '4px',
              bottom: '4px',
              left: '4px',
              width: 'calc(50% - 4px)',
              backgroundColor: '#ffffff',
              borderRadius: '999px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              transform: viewMode === 'week' ? 'translateX(0)' : 'translateX(100%)',
              transition: 'transform 0.3s cubic-bezier(0.4, 0.0, 0.2, 1)'
            }} />
            <div style={{ zIndex: 1, flex: 1, textAlign: 'center', padding: '6px 0', fontSize: '0.85rem', fontWeight: viewMode === 'week' ? 600 : 500, color: viewMode === 'week' ? 'var(--color-primary)' : '#64748b', transition: 'color 0.2s' }}>Semana</div>
            <div style={{ zIndex: 1, flex: 1, textAlign: 'center', padding: '6px 0', fontSize: '0.85rem', fontWeight: viewMode === 'month' ? 600 : 500, color: viewMode === 'month' ? 'var(--color-primary)' : '#64748b', transition: 'color 0.2s' }}>Mes</div>
          </div>

          {/* FILTRO CATEGORÍA */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            padding: '0 2px',
            flex: 1,
            justifyContent: 'center'
          }}>
            <label style={{ fontSize: '0.8rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
              Filtrar por:
            </label>
            <select
              value={filtroCategoria}
              onChange={(e) => setFiltroCategoria(e.target.value)}
              className="intra-select"
              style={{
                width: '100%',
                maxWidth: '220px',
                padding: '6px 12px',
                fontSize: '0.85rem',
                fontWeight: 600,
                backgroundColor: filtroCategoria === "Todas" ? '#fff' : getCategoriaStyles(filtroCategoria).bg,
                color: filtroCategoria === "Todas" ? '#475569' : getCategoriaStyles(filtroCategoria).text,
                borderLeft: '4px solid',
                borderLeftColor: filtroCategoria === "Todas" ? '#cbd5e1' : getCategoriaStyles(filtroCategoria).border,
                boxShadow: '0 2px 5px rgba(0,0,0,0.05)',
                borderRadius: '8px'
              }}
            >
              <option value="Todas">Todas las categorías</option>
              {CATEGORIES.map(cat => (
                <option key={cat.value} value={cat.value}>
                  {cat.label}
                </option>
              ))}
            </select>
          </div>

          {/* BOTONES ACCIÓN Y NAV */}
          <div className="intra-calendar-header-controls" style={{ flexShrink: 0 }}>
            <button
              type="button"
              className="btn-action"
              onClick={() => {
                resetFormToNewEvent();
                setShowModal(true);
              }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                height: '36px'
              }}
            >
              <span style={{ fontSize: '1.2rem', lineHeight: 1 }}>+</span>
              <span>Nuevo evento</span>
            </button>

            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div className="intra-calendar-nav" style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px' }}>
                <button
                  type="button"
                  className="intra-button-ghost"
                  onClick={() => changePeriod(-1)}
                  style={{ width: '28px', height: '28px', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%' }}
                >
                  ‹
                </button>
                <button
                  type="button"
                  className="intra-button-ghost"
                  onClick={goToToday}
                  style={{ fontSize: '0.85rem', fontWeight: 600, padding: '4px 12px', height: '28px' }}
                >
                  Hoy
                </button>
                <button
                  type="button"
                  className="intra-button-ghost"
                  onClick={() => changePeriod(1)}
                  style={{ width: '28px', height: '28px', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%' }}
                >
                  ›
                </button>
              </div>
              <p className="intra-calendar-range" style={{ margin: 0, fontSize: '0.75rem', color: '#64748b', whiteSpace: 'nowrap' }}>
                {formatFullDate(anchorDate)}
              </p>
            </div>
          </div>

        </div>
      </header>

      {/* LAYOUT PRINCIPAL */}
      <section className="intra-calendar-layout">
        {/* GRID DEL CALENDARIO */}
        <div className="intra-calendar-grid">
          {viewMode === "week" ? (
            <>
              {/* Cuerpo semana con header sincronizado */}
              <div className="intra-calendar-week-wrapper">
                {/* FILA 1: Header con spacer + dias (AZUL - NO SCROLL) */}
                <div className="intra-calendar-week-header-row">
                  {/* Spacer izquierdo para alinear con columna de horas */}
                  <div className="intra-calendar-hours-header-spacer"></div>

                  {/* Header de das - scrolleable horizontalmente pero fijo verticalmente */}
                  <div className="intra-calendar-days-header-wrapper">
                    <div className="intra-calendar-days-header">
                      {daysOfWeek.map((day, idx) => (
                        <div key={idx} className="intra-calendar-day-header">
                          <span className="intra-calendar-day-name">
                            {dayNamesShort[day.getDay()]}
                          </span>
                          <span className="intra-calendar-day-number">
                            {day.getDate()}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* FILA 2: Contenido scrolleable - horas + eventos (ROJO - SI SCROLL) */}
                <div className="intra-calendar-week-body">
                  {/* Columna de horas fija a la izquierda */}
                  <div className="intra-calendar-hours-column">
                    {HOURS.map((h) => (
                      <div key={h} className="intra-calendar-hour-row">
                        <span className="intra-calendar-hour-label">
                          {formatHourLabel(h)}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* Columnas de eventos - scrolleable */}
                  <div className="intra-calendar-days-body-wrapper">
                    <div className="intra-calendar-days-columns">
                      {daysOfWeek.map((day, dayIndex) => (
                        <div key={dayIndex} className="intra-calendar-day-column">
                          {/* celdas clicables */}
                          {HOURS.map((h) => (
                            <div
                              key={h}
                              className="intra-calendar-cell"
                              onClick={() =>
                                handleCrearEventoDesdeCelda(day, h)
                              }
                            ></div>
                          ))}

                          {/* eventos del da */}
                          {eventosSemana
                            .filter((ev) => {
                              const comps = getLiteralDateComponents(ev.fechaInicio);
                              if (!comps) return false;
                              return comps.year === day.getFullYear() &&
                                comps.month === (day.getMonth() + 1) &&
                                comps.day === day.getDate();
                            })
                            .map((ev) => {
                              const styles = getCategoriaStyles(ev.categoria);
                              return (
                                <div
                                  key={ev.id}
                                  className={
                                    "intra-calendar-event-block " +
                                    "intra-calendar-event-block--" +
                                    getCategoriaClass(ev.categoria) +
                                    (selectedEventId === ev.id
                                      ? " intra-calendar-event-block--selected"
                                      : "")
                                  }
                                  style={{
                                    ...getEventStyle(ev),
                                    backgroundColor: styles.bg,
                                    borderLeft: `3px solid ${styles.border}`,
                                    color: styles.text,
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                                    borderRadius: '4px',
                                    padding: '4px 8px',
                                    overflow: 'hidden'
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSeleccionarEvento(ev);
                                  }}
                                >
                                  <p className="intra-calendar-event-title" style={{ color: styles.text, fontSize: '10px', fontWeight: 700 }}>
                                    {ev.titulo}
                                  </p>
                                  {ev.ubicacion && (
                                    <p className="intra-calendar-event-location" style={{ color: styles.text, opacity: 0.8, fontSize: '9px' }}>
                                      {ev.ubicacion}
                                    </p>
                                  )}
                                </div>
                              );
                            })}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

            </>
          ) : (
            <>
              {/* Cabecera días mes */}
              <div className="intra-calendar-month-header">
                {dayNamesShort.map((name) => (
                  <div
                    key={name}
                    className="intra-calendar-month-header-cell"
                  >
                    {name.toUpperCase()}
                  </div>
                ))}
              </div>

              {/* Cuerpo mes */}
              <div className="intra-calendar-month-body">
                {monthMatrix.map((week, wIdx) => (
                  <div key={wIdx} className="intra-calendar-month-row">
                    {week.map((day, dIdx) => {
                      const isCurrentMonth =
                        day.getMonth() === anchorDate.getMonth();
                      const key = `${day.getFullYear()}-${(day.getMonth() + 1)
                        .toString()
                        .padStart(2, "0")}-${day
                          .getDate()
                          .toString()
                          .padStart(2, "0")}`;
                      const eventsForDay = eventosPorDiaMes.get(key) || [];

                      const isToday = isSameDay(day, new Date());

                      return (
                        <div
                          key={dIdx}
                          className={
                            "intra-calendar-month-cell" +
                            (isCurrentMonth
                              ? ""
                              : " intra-calendar-month-cell--faded") +
                            (isToday
                              ? " intra-calendar-month-cell--today"
                              : "")
                          }
                          onClick={() => handleSelectDayFromMonth(day)}
                        >
                          <div className="intra-calendar-month-cell-header">
                            <span className="intra-calendar-month-day-number">
                              {day.getDate()}
                            </span>
                          </div>

                          <div className="intra-calendar-month-events">
                            {eventsForDay
                              .sort((a, b) => {
                                const ta = parseDateTimeFlexible(a.fechaInicio);
                                const tb = parseDateTimeFlexible(b.fechaInicio);
                                return ta - tb;
                              })
                              .slice(0, 2)
                              .map((ev) => {
                                const styles = getCategoriaStyles(ev.categoria);
                                return (
                                  <button
                                    key={ev.id}
                                    type="button"
                                    className={
                                      "intra-calendar-month-event-pill " +
                                      "intra-calendar-month-event-pill--" +
                                      getCategoriaClass(ev.categoria)
                                    }
                                    style={{
                                      backgroundColor: styles.bg,
                                      color: styles.text,
                                      border: 'none',
                                      borderLeft: `3px solid ${styles.border}`,
                                      boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                                      borderRadius: '4px',
                                      padding: '3px 8px',
                                      marginBottom: '2px',
                                      display: 'flex',
                                      flexDirection: 'column',
                                      alignItems: 'flex-start',
                                      width: '100%',
                                      textAlign: 'left',
                                      minHeight: '22px',
                                      transition: 'all 0.2s ease'
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleSeleccionarEvento(ev);
                                    }}
                                  >
                                    <span className="intra-calendar-month-event-text" style={{ 
                                      fontWeight: 700, 
                                      fontSize: '10.5px',
                                      lineHeight: '1.2',
                                      display: '-webkit-box',
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: 'vertical',
                                      overflow: 'hidden',
                                      wordBreak: 'break-word'
                                    }}>
                                      {ev.titulo}
                                    </span>
                                  </button>
                                );
                              })}
                            {eventsForDay.length > 2 && (
                               <span className="intra-calendar-month-more" style={{ fontSize: '10px', paddingLeft: '4px', fontWeight: 600, opacity: 0.7 }}>
                                 + {eventsForDay.length - 2} más
                               </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* MODAL PARA CREAR/EDITAR EVENTO */}
      {showModal && (
        <div className="intra-modal-backdrop" onClick={() => setShowModal(false)}>
          <div className="intra-modal" onClick={(e) => e.stopPropagation()}>
            <div className="intra-modal-header">
              <h2 className="intra-modal-title">
                {selectedEventId ? "Editar evento" : "Nuevo evento corporativo"}
              </h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <button
                  type="button"
                  className="intra-modal-close"
                  onClick={() => setShowModal(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '2rem',
                    lineHeight: 1,
                    color: '#64748b',
                    cursor: 'pointer',
                    padding: 0,
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  ×
                </button>
              </div>
            </div>

            <form onSubmit={handleGuardarEvento} className="intra-modal-body">
              <div className="intra-form-group">
                <label className="intra-label" htmlFor="tituloEvento">
                  Título del evento
                </label>
                <input
                  id="tituloEvento"
                  name="titulo"
                  type="text"
                  className="intra-input"
                  value={formData.titulo}
                  onChange={handleFormChange}
                  placeholder="Ej. Junta de resultados trimestrales"
                />
                {errores.titulo && (
                  <span className="intra-error-text">{errores.titulo}</span>
                )}
              </div>

              <div className="intra-form-group">
                <label className="intra-label" htmlFor="descripcionEvento">
                  Descripción
                </label>
                <textarea
                  id="descripcionEvento"
                  name="descripcion"
                  className="intra-textarea"
                  rows={3}
                  value={formData.descripcion}
                  onChange={handleFormChange}
                  placeholder="Detalle del objetivo, asistentes, agenda, etc."
                />
              </div>

              {/* Fila con 3 campos: Fecha y hora, Categoría y Responsable - Responsivo */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                <div className="intra-form-group">
                  <label className="intra-label" htmlFor="fechaInicio">
                    Fecha y hora
                  </label>
                  <input
                    id="fechaInicio"
                    name="fechaInicio"
                    type="datetime-local"
                    className="intra-input"
                    value={formData.fechaInicio}
                    onKeyDown={(e) => e.preventDefault()}
                    onChange={handleDateChange}
                  />
                  {errores.fechaInicio && (
                    <span className="intra-error-text">{errores.fechaInicio}</span>
                  )}
                </div>

                <div className="intra-form-group">
                  <label className="intra-label" htmlFor="categoriaEvento">
                    Categoría
                  </label>
                  <select
                    id="categoriaEvento"
                    name="categoria"
                    className="intra-select"
                    value={formData.categoria}
                    onChange={handleFormChange}
                    style={{
                      borderLeft: '4px solid',
                      borderLeftColor: getCategoriaStyles(formData.categoria).border,
                      backgroundColor: getCategoriaStyles(formData.categoria).bg
                    }}
                  >
                    {CATEGORIES.map((cat) => (
                      <option key={cat.value} value={cat.value}>
                        {cat.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="intra-form-group">
                  <label className="intra-label" htmlFor="responsableEvento">
                    Responsable (área)
                  </label>
                  <select
                    id="responsableEvento"
                    name="responsable"
                    className="intra-select"
                    value={formData.responsable}
                    onChange={handleFormChange}
                  >
                    <option value="">Selecciona un área...</option>
                    {areasNegocio.map((area) => (
                      <option key={area.idAreaNegocio} value={area.nombre}>
                        {area.nombre}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="intra-form-group">
                <label className="intra-label" htmlFor="ubicacionEvento">
                  Ubicación
                </label>
                <input
                  id="ubicacionEvento"
                  name="ubicacion"
                  type="text"
                  className="intra-input"
                  value={formData.ubicacion}
                  onChange={handleFormChange}
                  placeholder="Ej. Sala A, Teams..."
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1.5rem' }}>
                {selectedEventId && (
                  <button
                    type="button"
                    className="intra-button-primary"
                    onClick={() => handleEliminarEvento(selectedEventId)}
                    style={{
                      backgroundColor: '#ef4444',
                      borderColor: '#ef4444',
                      color: '#ffffff'
                    }}
                  >
                    Eliminar
                  </button>
                )}

                <button
                  type="submit"
                  className="intra-button-primary"
                  style={{
                    backgroundColor: 'var(--color-primary)',
                    borderColor: 'var(--color-primary)',
                    minWidth: '120px'
                  }}
                >
                  {selectedEventId ? "Guardar cambios" : "Crear evento"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* DIALOGS */}
      <AlertDialog
        open={alertDialog.open}
        title={alertDialog.title}
        message={alertDialog.message}
        variant={alertDialog.variant}
        onClose={alertDialog.onClose}
      />

      <ConfirmDeleteDialog
        open={showConfirmDelete}
        onClose={() => setShowConfirmDelete(false)}
        onConfirm={ejecutarEliminacionEvento}
        roleName={eventoSeleccionado?.titulo}
      />
    </div>
  );
};

export default CalendarioCorporativo;

